import { Box, Button, Center, Heading, Spinner, Stack, Text, VStack } from '@chakra-ui/react'
import { UserAvatar } from '@src/components/sco/Account/UserAvatar'
import { SidebarNestedNavList, SidebarNestedNavListSection } from '@src/components/sco/SidebarNestedNavList'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import moment from 'moment'
import React from 'react'
import {
  LuBot,
  LuBriefcase,
  LuCircleHelp,
  LuComponent,
  LuCreditCard,
  LuFile,
  LuHammer,
  LuKeyRound,
  LuLock,
  LuShield,
  LuTrash,
  LuUser,
} from 'react-icons/lu'

type AccountNavProps = {}

export function getAccountNavItems(options?: { isAdmin?: boolean }): SidebarNestedNavListSection[] {
  const items: SidebarNestedNavListSection[] = [
    {
      title: 'Overview',
      icon: () => <LuComponent />,
      href: `/account`,
      activeExact: true,
    },
    {
      title: 'Startups Unlimited',
      icon: () => <LuShield />,
      href: `/account/startups-unlimited`,
      activeExact: true,
    },
    {
      title: 'Email & Login',
      icon: () => <LuLock />,
      href: `/account/security`,
      activeExact: true,
    },
    {
      title: 'Personal Profile',
      icon: () => <LuUser />,
      href: `/account/profile`,
      activeExact: true,
    },
    {
      title: 'Skills & Experiences',
      icon: () => <LuHammer />,
      href: `/account/skills`,
      activeExact: true,
    },
    {
      title: 'Startup Profile',
      icon: () => <LuBriefcase />,
      href: `/account/startup`,
      activeExact: true,
    },
    // {
    //   title: 'Your Interests & Goals',
    //   icon: () => <LuTags />,
    //   href: `/account/goals`,
    //   activeExact: true,
    // },
    {
      title: 'Billing',
      icon: () => <LuCreditCard />,
      href: `/account/billing`,
    },
    {
      title: 'My Files',
      icon: () => <LuFile />,
      href: `/account/files`,
      activeExact: true,
    },
    {
      title: 'My Tool Sessions',
      icon: () => <LuBot />,
      href: `/account/tool-sessions`,
      activeExact: true,
    },
    // {
    //   title: 'Notifications',
    //   icon: () => <LuBell />,
    //   href: `/account/notifications`,
    //   activeExact: true,
    // },
    {
      title: 'Support & Feedback',
      icon: () => <LuCircleHelp />,
      href: `/account/support`,
    },
    {
      title: 'Delete Account',
      icon: () => <LuTrash />,
      href: `/account/delete`,
    },
  ]

  if (options?.isAdmin) {
    // Insert before Support / Delete so admin tooling sits near the bottom of main settings
    const insertAt = items.findIndex(i => i.href === '/account/support')
    const apiTokensItem: SidebarNestedNavListSection = {
      title: 'API Tokens',
      icon: () => <LuKeyRound />,
      href: `/account/api-tokens`,
      activeExact: true,
    }
    if (insertAt === -1) {
      items.push(apiTokensItem)
    } else {
      items.splice(insertAt, 0, apiTokensItem)
    }
  }

  return items
}

/** @deprecated Prefer getAccountNavItems({ isAdmin }) — static list without admin-only items */
export const accountNavItems = getAccountNavItems()

export const AccountNav = (props: AccountNavProps) => {
  const { currentUser } = useCurrentUser()

  if (!currentUser) {
    return (
      <Center paddingY={'10em'}>
        <Spinner />
      </Center>
    )
  }

  const navItems = getAccountNavItems({ isAdmin: !!currentUser.isAdmin })

  return (
    <VStack align={'stretch'} gap={0} overflowY={'auto'} width={'full'}>
      <VStack
        align={'stretch'}
        bgColor={'colorPalette.container'}
        border={'1px solid {colors.colorPalette.border/50}'}
        borderRadius={'lg'}
        boxShadow={'xs'}
        gap={4}
        padding={'1em'}
      >
        <Stack align={'center'} direction={['column', 'column', 'row']} gap={2}>
          <Box>
            <UserAvatar
              disablePopover={true}
              disablePresence={true}
              user={currentUser}
              avatarProps={{
                bgColor: 'colorPalette.subtle',
                boxShadow: 'sm',
                size: 'md',
              }}
            />
          </Box>
          <Box flexGrow={1}>
            <Heading size={'sm'} wordBreak={'break-word'}>
              {currentUser?.fullName || currentUser?.email}
            </Heading>
            <Text fontSize={'xs'} marginTop={'0.125em'} opacity={'0.5'}>
              {'Member since '}
              {moment(currentUser?.createdAt).format('Y')}
            </Text>
          </Box>
        </Stack>
        <Button asChild size={'sm'} variant={'outline'}>
          <NextLink href={`/members/${currentUser?.slug || currentUser?.userId}`}>View My Profile</NextLink>
        </Button>
      </VStack>
      <Box paddingY={'1em'}>
        <SidebarNestedNavList sections={navItems} />
      </Box>
    </VStack>
  )
}
